import React, { useEffect, useState } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
} from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { getShiftNotes } from 'core/API/logbook';
import { useQuery } from 'react-query';
import { useWorkplaceUser } from 'module/documents/utils';
import { formatDateToMMDDYYYY } from 'shared/utils/dates/formatDate';


const ViewShiftNotes = () => {
  const { noteId } = useParams<{ noteId?: string }>();
  const history = useHistory();
  const workplace = useWorkplaceUser();
  const workplaceId = parseInt(localStorage.getItem('workplace-id') || '1'); // Dynamic workplace ID from localStorage
  
  const [author, setAuthor] = useState('');
  const [noteDate, setNoteDate] = useState('');
  const [noteContent, setNoteContent] = useState('');

  const { data: shiftNotesData } = useQuery(
    ['shiftNotes', workplaceId],
    () => getShiftNotes({ workplaceId }),
    { enabled: !!workplaceId && !!noteId }
  );

  useEffect(() => {
    if (!noteId || !shiftNotesData) return;
    const list = Array.isArray(shiftNotesData?.member) ? shiftNotesData.member : [];
    const match = list.find((n: any) => {
      const idFromAtId = n['@id'] ? parseInt(String(n['@id']).split('/').pop()) : undefined;
      return idFromAtId === Number(noteId);
    });
    if (match) {
      const authorText = match.author ? 
        (typeof match.author === 'object' && match.author.firstName && match.author.lastName)
          ? `${match.author.firstName} ${match.author.lastName}`.trim()
          : (typeof match.author === 'object' && match.author.firstName)
            ? match.author.firstName
            : (typeof match.author === 'string' && match.author.includes('/'))
              ? `User ${match.author.split('/').pop()}`
              : (typeof match.author === 'string')
                ? match.author
                : 'Unknown'
        : 'Unknown';
      setAuthor(authorText);
      setNoteDate(match.date || match.date_of_the_note || '');
      
      const content = match.note || match.note_details || '';
      setNoteContent(content);
    }
  }, [noteId, shiftNotesData]);

  const formatDate = (dateString: string) => {
    return formatDateToMMDDYYYY(dateString);
  };

  // Function to check if content contains HTML tags
  const hasHtmlTags = (content: string) => {
    if (!content) return false;
    return /<[^>]*>/g.test(content);
  };

  return (
    <Box sx={{ backgroundColor: '#f4f6fa', minHeight: '100vh', py: 3 }}>
      <Box maxWidth="1100px" mx="auto">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Button
            onClick={() => history.goBack()}
            sx={{
              backgroundColor: 'rgb(68, 99, 136)',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              py: 0.75,
              '&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
            }}
          >
            Back
          </Button>
        </Box>

        <Paper elevation={1}>
          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.100',
              borderBottom: 1,
              borderColor: 'divider',
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" fontWeight={600}>
                View Shift Note
              </Typography>
            </Box>
          </Box>
          
          <Box
            sx={{
              px: 3,
              pt: 1.5,
              pb: 0.1
            }}
          >
            <Typography variant="subtitle2">
              <strong>{author}</strong> — {formatDate(noteDate)}
            </Typography>
          </Box>
          
          <Box sx={{ p: 3 }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                backgroundColor: '#f9f9f9',
                minHeight: '200px'
              }}
            >
              {hasHtmlTags(noteContent) ? (
                <div
                  dangerouslySetInnerHTML={{ __html: noteContent }}
                  style={{ 
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    lineHeight: '1.5'
                  }}
                />
              ) : (
                <div
                  style={{ 
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    lineHeight: '1.5'
                  }}
                >
                  {noteContent}
                </div>
              )}
            </Paper>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default ViewShiftNotes;